import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Trash2 } from 'lucide-react';

/**
 * Reusable delete button + type-to-confirm modal.
 *
 * Props:
 * ─ `title`            – short item kind shown in the dialog heading ("tenant", "user"…)
 * ─ `description`      – optional rich body replacing the default "irreversible" line
 * ─ `confirmText`      – exact text the user must type (default: "im sure"). Case-insensitive.
 * ─ `onConfirm`        – fired when user types confirmText and clicks Delete
 * ─ `isBusy`           – external busy flag (disables buttons, shows "Deleting…")
 * ─ `errorMessage`     – optional error rendered under the input
 * ─ `triggerContent`   – override the trigger's visual (default: <Trash2/> icon)
 * ─ `triggerTitle`     – trigger tooltip (default: "Delete")
 * ─ `triggerClassName` – override trigger button styles
 */
export default function ConfirmDeleteButton({
  title = 'item',
  description,
  confirmText = 'im sure',
  onConfirm,
  isBusy = false,
  errorMessage,
  triggerContent,
  triggerTitle = 'Delete',
  triggerClassName,
}: {
  title?: string;
  description?: React.ReactNode;
  confirmText?: string;
  onConfirm: () => void | Promise<void>;
  isBusy?: boolean;
  errorMessage?: string | null;
  triggerContent?: React.ReactNode;
  triggerTitle?: string;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [txt,  setTxt]  = useState('');

  const required = String(confirmText || '').trim();
  const requiredLower = required.toLowerCase();
  const matches = txt.trim().toLowerCase() === requiredLower;

  const reset = () => { setTxt(''); setOpen(false); };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => {
      if (isBusy && !v) return;
      setOpen(v);
      if (!v) setTxt('');
    }}>
      {/* ── trigger ───────────────────────────────────────── */}
      <Dialog.Trigger asChild>
        <button
          title={triggerTitle}
          className={triggerClassName ?? 'p-1 rounded text-neutral-500 hover:text-rose-500 transition-colors'}
        >
          {triggerContent ?? <Trash2 className="h-4 w-4" />}
        </button>
      </Dialog.Trigger>

      {/* ── modal ─────────────────────────────────────────── */}
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        <Dialog.Content
          className="
            fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
            w-96 rounded-md bg-black border border-neutral-800
            p-6 shadow-xl space-y-4
          "
          onInteractOutside={(e) => { if (isBusy) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (isBusy) e.preventDefault(); }}
        >
          <Dialog.Title className="text-lg font-semibold">
            Delete this {title}?
          </Dialog.Title>

          {description ? (
            <div className="text-sm text-neutral-300 space-y-1">{description}</div>
          ) : (
            <p className="text-sm text-neutral-400">
              This action is <span className="text-rose-400">irreversible</span>.
            </p>
          )}

          <div>
            <label className="block text-xs text-neutral-400 mb-1">
              Type <code className="font-mono text-rose-400">{required}</code> to confirm:
            </label>
            <input
              autoFocus
              value={txt}
              onChange={e => setTxt(e.target.value)}
              placeholder={required}
              disabled={isBusy}
              className="
                w-full bg-black border border-neutral-700 rounded px-3 py-2
                font-mono text-sm
                focus:outline-none focus:ring-2 focus:ring-rose-500
                disabled:opacity-50
              "
            />
          </div>

          {errorMessage && (
            <p className="text-xs text-rose-400">{errorMessage}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Dialog.Close asChild>
              <button
                onClick={reset}
                disabled={isBusy}
                className="px-3 py-1 rounded bg-black border border-neutral-700 text-neutral-300 hover:bg-rose-600 hover:border-rose-600 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </Dialog.Close>

            <button
              disabled={!matches || isBusy}
              onClick={async () => {
                await onConfirm();
                reset();
              }}
              className={`
                px-3 py-1 rounded transition-colors
                ${matches && !isBusy
                  ? 'bg-black border border-neutral-700 text-neutral-300 hover:bg-rose-600 hover:border-rose-600 hover:text-white'
                  : 'bg-black border border-neutral-700 text-neutral-600 cursor-not-allowed'}
              `}
            >
              {isBusy ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
